import { getVisualAssetDefinition } from '../../../visualAssets/catalog';
import { getVisualAssetImage } from '../visualAssetImages';
import { isRectVisible, type ScreenBounds } from '../culling';
import { getChestMarkerSprite, getNpcMarkerSprite } from '../runtimeSpriteCache';
import type { MapLayerRenderer } from '../MapLayerRenderer';
import type { Viewport } from '../viewport';

export class WorldMarkerRenderer {
    constructor(private readonly tileSize: number) {}

    public drawVisualAssets(
        ctx: CanvasRenderingContext2D,
        assets: Array<{ assetId: string; mapX: number; mapY: number }>,
        viewport: Viewport,
        viewportBounds: ScreenBounds
    ): void {
        for (const asset of assets) {
            const definition = getVisualAssetDefinition(asset.assetId);
            const image = getVisualAssetImage(asset.assetId);
            if (!definition || !image) {
                continue;
            }

            const center = viewport.worldCenterToScreen({
                x: asset.mapX * this.tileSize,
                y: asset.mapY * this.tileSize,
            });
            const maxDrawSize = this.tileSize * definition.drawScale;
            const imageWidth = Math.max(1, image.naturalWidth || image.width || 1);
            const imageHeight = Math.max(1, image.naturalHeight || image.height || 1);
            const imageAspect = imageWidth / imageHeight;
            const drawWidth = imageAspect >= 1 ? maxDrawSize : maxDrawSize * imageAspect;
            const drawHeight = imageAspect >= 1 ? maxDrawSize / imageAspect : maxDrawSize;
            const drawX = center.x - drawWidth / 2;
            const drawY = center.y - drawHeight / 2;
            if (!isRectVisible(viewportBounds, drawX, drawY, drawWidth, drawHeight)) {
                continue;
            }

            ctx.save();
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(image, Math.round(drawX), Math.round(drawY), Math.round(drawWidth), Math.round(drawHeight));
            ctx.restore();
        }
    }

    public drawNpcs(ctx: CanvasRenderingContext2D, npcs: any[], viewport: Viewport, mapLayer: MapLayerRenderer, viewportBounds: ScreenBounds): void {
        const grid = mapLayer.getGrid();
        const emojis: Record<string, string> = {
            merchant: '🏪',
            guard: '🛡️',
            innkeeper: '🍺',
            villager: '👨',
            quest_giver: '❓',
            town_wall_h: '🧱',
            town_wall_v: '🧱',
            town_gate: '🚪',
            shrine: '⛩️',
            fountain: '⛲',
            market_stall: '🏪',
        };

        for (const npc of npcs) {
            const screen = viewport.worldToScreen(npc.x, npc.y, grid);
            const widthPx = npc.width * this.tileSize;
            const heightPx = npc.height * this.tileSize;
            if (!isRectVisible(viewportBounds, screen.x, screen.y, widthPx, heightPx)) {
                continue;
            }

            const marker = getNpcMarkerSprite(emojis[npc.npcType] || '👤', widthPx, heightPx);
            ctx.drawImage(marker.canvas, screen.x, screen.y, marker.logicalWidth, marker.logicalHeight);
        }
    }

    public drawLootChests(
        ctx: CanvasRenderingContext2D,
        chests: { x: number; y: number }[],
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
        viewportBounds: ScreenBounds
    ): void {
        const grid = mapLayer.getGrid();
        for (const chest of chests) {
            const center = viewport.worldToScreenCenter(chest.x, chest.y, grid);
            if (!isRectVisible(viewportBounds, center.x - 10, center.y - 10, 20, 20)) {
                continue;
            }
            const marker = getChestMarkerSprite();
            ctx.drawImage(
                marker.canvas,
                center.x - marker.logicalWidth / 2,
                center.y - marker.logicalHeight / 2,
                marker.logicalWidth,
                marker.logicalHeight,
            );
        }
    }
}
